import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Card,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Table,
  Button,
  Tabs,
  Tab,
} from "react-bootstrap";
import moment from "moment";
import {
  getTicketThread,
  getTasksByTicketId,
  getTicketByTicketId,
  getBuildingBlockByTicket,
} from "../Api/TicketApiOrg";
import { useAuth } from "../Context/AuthContext";
import TicketEditModal from "./TicketEditModal";
import "./TicketInfoPage.css";
import { attachmentApi } from "../Api/TicketApiOrg";
import { toast } from "react-toastify";
import { Accordion } from "react-bootstrap";
import { addTicketRemark, getTicketRemarks } from "../Api/TicketApiOrg";

/* ================= HELPERS ================= */

const getPriorityBadge = (priority) => {
  const variants = {
    critical: "danger",
    high: "warning",
    medium: "primary",
    low: "secondary",
    normal: "info",
  };

  return (
    <Badge bg={variants[priority?.toLowerCase()] || "secondary"}>
      {priority || "N/A"}
    </Badge>
  );
};

const getStatusBadge = (status) => {
  const variants = {
    open: "success",
    closed: "secondary",
    "in progress": "warning",
    resolved: "info",
  };

  return (
    <Badge bg={variants[status?.toLowerCase()] || "secondary"}>
      {status || "N/A"}
    </Badge>
  );
};

const formatDateTime = (date, time) => {
  if (!date) return "N/A";
  return `${moment(date).format("MMM DD, YYYY")} ${
    time?.substring(0, 8) || ""
  }`;
};

/* ================= PAGE ================= */

const TicketInfoPage = () => {
  const { ticketId } = useParams();
  const { user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [ticketLogs, setTicketLogs] = useState([]);
  const [ticketTasks, setTicketTasks] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const [ticketDetails, setTicketDetails] = useState(null);

  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  const [buildingBlock, setBuildingBlock] = useState(null);

  /* SAFE STATES FOR MODAL */

  const [projectOptions, setProjectOptions] = useState([]);
  const [ticketAssignees, setTicketAssignees] = useState([]);

  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const [remarks, setRemarks] = useState([]);
  const [newRemark, setNewRemark] = useState("");
  const [savingRemark, setSavingRemark] = useState(false);
  /* ================= FETCH DATA ================= */

  const loadData = async () => {
    try {
      setLoading(true);

      const thread = await getTicketThread(ticketId);

      setTicket(thread?.ticket || null);
      setMessages(thread?.messages || []);
      setTicketLogs(thread?.logs || []);

      const tasks = await getTasksByTicketId(ticketId);
      setTicketTasks(tasks || []);

      const details = await getTicketByTicketId(ticketId);
      setTicketDetails(details);

      const block = await getBuildingBlockByTicket(ticketId);
      setBuildingBlock(block);

      const remarksData = await getTicketRemarks(ticketId);
      setRemarks(remarksData || []);

      const attachmentData = await attachmentApi.getTicketAttachments(ticketId);

      if (attachmentData?.attachments) {
        setTicketAttachments(attachmentData.attachments);
      }
    } catch (err) {
      console.error("Failed to load ticket", err);
    } finally {
      setLoading(false);
    }
  };

  // const loadData = async () => {
  //   try {
  //     setLoading(true);

  //     const thread = await getTicketThread(ticketId);

  //     setTicket(thread?.ticket || null);
  //     setMessages(thread?.messages || []);
  //     setTicketLogs(thread?.logs || []);

  //     const tasks = await getTasksByTicketId(ticketId);
  //     setTicketTasks(tasks || []);

  //     const details = await getTicketByTicketId(ticketId);
  //     setTicketDetails(details);

  //     // 🔹 FETCH ATTACHMENTS
  //     const attachmentData = await attachmentApi.getTicketAttachments(ticketId);

  //     if (attachmentData?.attachments) {
  //       setTicketAttachments(attachmentData.attachments);
  //     }
  //   } catch (err) {
  //     console.error("Failed to load ticket", err);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();

    switch (ext) {
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "xls":
      case "xlsx":
        return "📊";
      case "jpg":
      case "png":
        return "🖼️";
      case "zip":
        return "📦";
      default:
        return "📎";
    }
  };

  const handleAddRemark = async () => {
    if (!newRemark.trim()) {
      toast.warning("Remark cannot be empty");
      return;
    }

    try {
      setSavingRemark(true);

      await addTicketRemark({
        ticketId: ticketId,
        orgId: user.organisationId,
        closureRemarks: newRemark,

        actionType: "REMARK",
        createdBy: String(user.userId),
        changedBy: String(user.userId),
      });

      toast.success("Remark added");

      setNewRemark("");

      const updatedRemarks = await getTicketRemarks(ticketId);
      setRemarks(updatedRemarks);
    } catch (err) {
      toast.error("Failed to add remark");
    } finally {
      setSavingRemark(false);
    }
  };

  const handleDownloadAttachment = async (blobUrl, fileName) => {
    try {
      const response = await attachmentApi.downloadAttachment(
        blobUrl,
        fileName,
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download attachment");
    }
  };

  const handleViewAttachment = async (blobUrl, fileName) => {
    try {
      const response = await attachmentApi.viewAttachment(blobUrl, fileName);

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });

      const url = window.URL.createObjectURL(blob);

      window.open(url, "_blank");

      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      toast.error("Failed to view attachment");
    }
  };

  useEffect(() => {
    if (user?.organisationId) {
      loadData();
    }
  }, [ticketId, user]);

  /* ================= LOADING ================= */

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner />
      </div>
    );
  }

  /* ================= NOT FOUND ================= */

  if (!ticket) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Ticket not found</Alert>
      </Container>
    );
  }

  /* ================= PAGE ================= */

  return (
    <Container fluid className="tdp-container">
      {" "}
      {/* HEADER */}
      <Card className="tdp-header-card shadow-sm mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={9}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "81vw",
                  alignItems: "center",
                }}
              >
                <h4>{ticket.ticketName}</h4>
                <Button onClick={() => setShowEditModal(true)}>
                  ✏ Edit Ticket
                </Button>
              </div>

              <div className="d-flex gap-2 mt-2">
                <Badge>{ticket.ticketId}</Badge>
                {getStatusBadge(ticket.status)}
                {getPriorityBadge(ticket.priority)}
              </div>

              <div className="mt-2 opacity-75">
                Created by <b>{ticket.createdBy}</b> •{" "}
                {moment(ticket.ticketTime).format("MMM DD YYYY HH:mm")}
              </div>
            </Col>

            <Col md={3} className="text-end"></Col>
          </Row>
        </Card.Body>
      </Card>
      {/* TABS */}
      <Card className="tdp-main-card shadow-sm">
        <Card.Body>
          <Tabs defaultActiveKey="overview">
            {/* OVERVIEW */}

            <Tab eventKey="overview" title="Overview">
              <Row className="mt-3 g-3">
                {/* LEFT SIDE */}
                <Col md={6}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6 className="mb-3">Ticket Information</h6>

                      <Row>
                        <Col sm={6}>
                          <p>
                            <b>Ticket ID</b>
                            <br />
                            {ticketDetails?.ticketId}
                          </p>
                        </Col>

                        <Col sm={6}>
                          <p>
                            <b>Status</b>
                            <br />
                            {getStatusBadge(ticketDetails?.status)}
                          </p>
                        </Col>

                        <Col sm={6}>
                          <p>
                            <b>Priority</b>
                            <br />
                            {getPriorityBadge(ticketDetails?.priority)}
                          </p>
                        </Col>

                        <Col sm={6}>
                          <p>
                            <b>Category</b>
                            <br />
                            {ticketDetails?.category || "N/A"}
                          </p>
                        </Col>

                        <Col sm={6}>
                          <p>
                            <b>Ticket Type</b>
                            <br />
                            {ticketDetails?.ticketType || "N/A"}
                          </p>
                        </Col>

                        <Col sm={6}>
                          <p>
                            <b>Reason</b>
                            <br />
                            {ticketDetails?.ticketReason || "N/A"}
                          </p>
                        </Col>

                        <Col sm={6}>
                          <p>
                            <b>Project</b>
                            <br />
                            {ticketDetails?.projectAssigned || "N/A"}
                          </p>
                        </Col>

                        <Col sm={6}>
                          <p>
                            <b>Due Date</b>
                            <br />
                            {ticketDetails?.dueDate
                              ? moment(ticketDetails.dueDate).format(
                                  "MMM DD YYYY",
                                )
                              : "N/A"}
                          </p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                {/* RIGHT SIDE */}
                <Col md={6}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6 className="mb-3">People & Email</h6>

                      <Row>
                        <Col sm={12}>
                          <p>
                            <b>Created By</b>
                            <br />
                            {ticketDetails?.createdBy}
                          </p>
                        </Col>

                        <Col sm={12}>
                          <p>
                            <b>Assigned To</b>
                            <br />
                            {ticketDetails?.assignedTo || "Unassigned"}
                          </p>
                        </Col>

                        <Col sm={12}>
                          <p>
                            <b>Submitted By</b>
                            <br />
                            {ticketDetails?.submittedBy || "N/A"}
                          </p>
                        </Col>

                        <Col sm={12}>
                          <p>
                            <b>Email Source</b>
                            <br />
                            {ticketDetails?.fromMail}
                          </p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                {/* DESCRIPTION */}
                {/* <Col md={12}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6>Description</h6>

                      {ticketDetails?.description
                        ? ticketDetails.description
                        : "No description available"}
                    </Card.Body>
                  </Card>
                </Col> */}

                <Col md={12}>
                  <Card className="tdp-info-card">
                    <Card.Body>
                      <h6 className="mb-3">Attachments</h6>

                      {loadingAttachments ? (
                        <Spinner />
                      ) : ticketAttachments.length > 0 ? (
                        ticketAttachments.map((att, i) => (
                          <Card key={i} className="mb-2">
                            <Card.Body className="py-2 d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <span
                                  style={{ fontSize: "20px" }}
                                  className="me-2"
                                >
                                  {getFileIcon(att.fileName)}
                                </span>

                                <div>
                                  <div className="fw-bold">{att.fileName}</div>

                                  <small className="text-muted">
                                    Uploaded by {att.createdBy}
                                  </small>
                                </div>
                              </div>

                              <div>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() =>
                                    handleViewAttachment(
                                      att.azureBlobUrl,
                                      att.fileName,
                                    )
                                  }
                                >
                                  👁️ View
                                </Button>

                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() =>
                                    handleDownloadAttachment(
                                      att.azureBlobUrl,
                                      att.fileName,
                                    )
                                  }
                                >
                                  ⬇️ Download
                                </Button>
                              </div>
                            </Card.Body>
                          </Card>
                        ))
                      ) : (
                        <p className="text-muted">No attachments found</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>

            <Tab eventKey="remarks" title="Remarks">
              <div className="mt-3">
                {/* Add Remark */}
                <Card className="mb-3">
                  <Card.Body>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Add remark..."
                      value={newRemark}
                      onChange={(e) => setNewRemark(e.target.value)}
                    />

                    <div className="text-end mt-2">
                      <Button onClick={handleAddRemark} disabled={savingRemark}>
                        {savingRemark ? "Saving..." : "Add Remark"}
                      </Button>
                    </div>
                  </Card.Body>
                </Card>

                {/* Remarks List */}

                {remarks.length > 0 ? (
                  remarks.map((remark) => (
                    <Card key={remark.ticketLogGuid} className="mb-2">
                      <Card.Body>
                        <div className="d-flex justify-content-between">
                          <div>
                            <strong>{remark.createdBy}</strong>

                            <div className="text-muted small">
                              {formatDateTime(
                                remark.createdDate,
                                remark.createdTime,
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="mt-2">{remark.closureRemarks}</div>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p className="text-muted">No remarks yet</p>
                )}
              </div>
            </Tab>

            {/* CONVERSATION */}

            <Tab eventKey="conversation" title="Conversation">
              <div className="mt-3">
                {messages.length > 0 ? (
                  <Accordion
                    alwaysOpen={false}
                    className="tdp-conversation-accordion"
                  >
                    {messages.map((msg, index) => (
                      <Accordion.Item
                        eventKey={index.toString()}
                        key={msg.messageGuid}
                        className="tdp-message-accordion"
                      >
                        <Accordion.Header>
                          <div className="conversation-header">
                            <div>
                              <strong>{msg.fromName || msg.fromMail}</strong>

                              <div className="text-muted small">
                                {moment(msg.createdOnDate).format(
                                  "MMM DD YYYY",
                                )}{" "}
                                {msg.createdOnTime?.substring(0, 8)}
                              </div>
                            </div>

                            {msg.isInternal && (
                              <Badge bg="warning" className="ms-2">
                                Internal
                              </Badge>
                            )}
                          </div>
                        </Accordion.Header>

                        <Accordion.Body>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: msg.rawHtmlBody || msg.body,
                            }}
                          />
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
                ) : (
                  <p>No messages</p>
                )}
              </div>
            </Tab>

            {/* ACTIVITY */}

            <Tab eventKey="activity" title="Activity Log">
              <Table bordered hover className="tdp-activity-table mt-3">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Action</th>
                    <th>Old</th>
                    <th>New</th>
                    <th>User</th>
                    <th>Effort</th>
                    <th>Date</th>
                  </tr>
                </thead>

                <tbody>
                  {ticketLogs.map((log, i) => (
                    <tr key={log.ticketLogGuid}>
                      <td>{i + 1}</td>

                      <td>
                        <Badge bg="secondary">
                          {log.actionType?.replace(/_/g, " ")}
                        </Badge>
                      </td>

                      <td>{log.oldValue || "-"}</td>
                      <td>{log.newValue || "-"}</td>
                      <td>{log.changedBy}</td>

                      <td>
                        {log.effortValue
                          ? `${log.effortValue} ${log.effortUnit}`
                          : "-"}
                      </td>

                      <td>
                        {formatDateTime(log.createdDate, log.createdTime)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Tab>

            {/* TASKS */}

            <Tab eventKey="tasks" title="Tasks">
              <div className="mt-3">
                {ticketTasks.length > 0 ? (
                  ticketTasks.map((task) => (
                    <Card key={task.taskId} className="tdp-task-card mb-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between">
                          <div>
                            <b>{task.taskName}</b>{" "}
                            <Badge bg="dark">{task.taskId}</Badge>
                          </div>

                          <div className="d-flex gap-2">
                            {getStatusBadge(task.taskStatus)}
                            {getPriorityBadge(task.taskPriority)}
                          </div>
                        </div>

                        <div className="text-muted mt-2">
                          Assigned To: {task.assignedTo || "N/A"}
                        </div>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p>No related tasks</p>
                )}
              </div>
            </Tab>

            <Tab eventKey="buildingBlock" title="Building Block">
              <div className="mt-3">
                {buildingBlock ? (
                  <Table bordered hover>
                    <thead className="table-light">
                      <tr>
                        <th>Block ID</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Project</th>
                        <th>Process POC</th>
                        <th>Tech POC</th>
                        <th>Type</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>{buildingBlock.buildingBlockId}</td>
                        <td>{buildingBlock.buildingBlockName}</td>
                        <td>
                          <Badge bg="secondary">
                            {buildingBlock.buildingBlockStatus}
                          </Badge>
                        </td>
                        <td>{buildingBlock.buildingBlockProject}</td>
                        <td>{buildingBlock.buildingBlockProcessPoc}</td>
                        <td>{buildingBlock.buildingBlockTechPoc}</td>
                        <td>{buildingBlock.buildingBlockTypes}</td>
                        <td>
                          {buildingBlock.actualStartDate
                            ? moment(buildingBlock.actualStartDate).format(
                                "MMM DD YYYY",
                              )
                            : "N/A"}
                        </td>
                        <td>
                          {buildingBlock.actualEndDate
                            ? moment(buildingBlock.actualEndDate).format(
                                "MMM DD YYYY",
                              )
                            : "N/A"}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                ) : (
                  <p>No Building Block mapped</p>
                )}
              </div>
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>
      {/* EDIT MODAL */}
      <TicketEditModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        ticket={ticket}
        orgId={user?.organisationId}
        userDetails={user}
        projectOptions={projectOptions}
        ticketAssignees={ticketAssignees}
        refreshTickets={loadData}
      />
    </Container>
  );
};

export default TicketInfoPage;

// import { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import {
//   Container,
//   Card,
//   Row,
//   Col,
//   Badge,
//   Spinner,
//   Alert,
//   Table,
//   Button,
//   Form,
// } from "react-bootstrap";
// import moment from "moment";
// import {
//   getTicketsByOrgId,
//   getTicketLogsByTicketId,
//   getTasksByTicketId,
//   getTicketThread,
//   addTicketReply,
// } from "../Api/TicketApiOrg";
// import { useAuth } from "../Context/AuthContext";
// import "./TicketInfoPage.css";
// import { toast } from "react-toastify";
// import TicketEditModal from "./TicketEditModal";

// const Section = ({ title, children }) => (
//   <div className="mb-4">
//     <h6 className="section-title">{title}</h6>
//     <div className="section-card">{children}</div>
//   </div>
// );

// const InfoRow = ({ label, value, children }) => (
//   <Row className="mb-2">
//     <Col md={4} className="text-muted fw-semibold">
//       {label}
//     </Col>
//     <Col md={8}>{children || value}</Col>
//   </Row>
// );

// const EmptyState = ({ text }) => <div className="empty-state">{text}</div>;

// /* ===================== HELPERS ===================== */

// const getPriorityBadge = (priority) => {
//   const variants = {
//     critical: "danger",
//     high: "warning",
//     medium: "primary",
//     low: "secondary",
//     normal: "info",
//   };
//   return (
//     <Badge bg={variants[priority?.toLowerCase()] || "secondary"}>
//       {priority}
//     </Badge>
//   );
// };

// const getStatusBadge = (status) => {
//   const variants = {
//     open: "success",
//     closed: "secondary",
//     "in progress": "warning",
//     resolved: "info",
//   };
//   return (
//     <Badge bg={variants[status?.toLowerCase()] || "secondary"}>{status}</Badge>
//   );
// };

// const formatDateTime = (date, time) => {
//   if (!date) return "N/A";
//   return `${moment(date).format("MMM DD, YYYY")} ${time?.substring(0, 8) || ""}`;
// };

// /* ===================== PAGE ===================== */

// const TicketInfoPage = () => {
//   const { ticketId } = useParams();
//   const navigate = useNavigate();
//   const { user } = useAuth();

//   const [ticket, setTicket] = useState(null);
//   const [ticketLogs, setTicketLogs] = useState([]);
//   const [ticketTasks, setTicketTasks] = useState([]);

//   const [loading, setLoading] = useState(true);

//   const [messages, setMessages] = useState([]);
//   const [attachments, setAttachments] = useState([]);

//   const [replyBody, setReplyBody] = useState("");
//   const [replyFiles, setReplyFiles] = useState([]);

//   const [showEditModal, setShowEditModal] = useState(false);

//   const [projectOptions, setProjectOptions] = useState([]);
//   const [ticketAssignees, setTicketAssignees] = useState([]);

//   /* ===================== FETCH DATA ===================== */

//   useEffect(() => {
//     const loadData = async () => {
//       try {
//         setLoading(true);

//         const thread = await getTicketThread(ticketId);

//         setTicket(thread.ticket);
//         setMessages(thread.messages || []);
//         setTicketLogs(thread.logs || []);
//         setAttachments(thread.attachments || []);

//         const tasks = await getTasksByTicketId(ticketId);
//         setTicketTasks(tasks || []);
//       } catch (err) {
//         console.error("❌ Failed to load ticket info", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (user?.organisationId) loadData();
//   }, [ticketId, user]);

//   /* ===================== UI ===================== */

//   if (loading) {
//     return (
//       <div className="text-center mt-5">
//         <Spinner animation="border" />
//       </div>
//     );
//   }

//   if (!ticket) {
//     return (
//       <Container className="mt-4">
//         <Alert variant="danger">Ticket not found.</Alert>
//       </Container>
//     );
//   }

//   const refreshTicketData = async () => {
//     try {
//       const thread = await getTicketThread(ticketId);

//       setTicket(thread.ticket);
//       setMessages(thread.messages || []);
//       setTicketLogs(thread.logs || []);
//       setAttachments(thread.attachments || []);

//       const tasks = await getTasksByTicketId(ticketId);
//       setTicketTasks(tasks || []);
//     } catch (err) {
//       console.error("Refresh failed", err);
//     }
//   };

//   const handleSendReply = async () => {
//     const formData = new FormData();
//     formData.append("messageBody", replyBody);
//     formData.append("senderEmail", user.email);

//     replyFiles.forEach((file) => {
//       formData.append("files", file);
//     });

//     await addTicketReply(ticketId, formData);

//     toast.success("Reply sent");

//     setReplyBody("");
//     setReplyFiles([]);

//     // Reload thread
//     const thread = await getTicketThread(ticketId);
//     setMessages(thread.messages);
//   };

//   return (
//     <Container fluid className="ticket-info-page mt-3">
//       {/* MAIN CARD */}
//       <Card className="shadow-sm border-0" style={{ marginTop: "5vw" }}>
//         {/* HEADER */}
//         <Card.Header className="bg-white border-bottom">
//           <div className="d-flex justify-content-between align-items-center">
//             <div>
//               <h5 className="mb-1 fw-bold">Ticket Information</h5>
//               <Badge pill bg="dark">
//                 {ticket.ticketId}
//               </Badge>
//             </div>

//             <div className="d-flex gap-2 align-items-center">
//               {getStatusBadge(ticket.status)}
//               {getPriorityBadge(ticket.priority)}

//               <Button
//                 size="sm"
//                 variant="outline-primary"
//                 onClick={() => setShowEditModal(true)}
//               >
//                 Edit Ticket
//               </Button>
//             </div>
//           </div>
//         </Card.Header>

//         <Card.Body>
//           {/* ================= BASIC INFO ================= */}
//           <Section title="📝 Ticket Details">
//             <InfoRow label="Ticket Name" value={ticket.ticketName} />
//             <InfoRow label="Ticket Type">
//               <Badge pill bg="info">
//                 {ticket.ticketType || "N/A"}
//               </Badge>
//             </InfoRow>
//             <InfoRow label="Created By" value={ticket.createdBy} />
//             <InfoRow label="Assigned To" value={ticket.assignedTo || "N/A"} />
//             <InfoRow label="From Email" value={ticket.fromMail || "N/A"} />
//             <InfoRow
//               label="Ticket Time"
//               value={moment(ticket.ticketTime).format("MMM DD, YYYY • HH:mm")}
//             />
//           </Section>

//           {/* ================= DESCRIPTION ================= */}
//           <Section title="💬 Conversation">
//             {messages.length > 0 ? (
//               messages.map((msg, index) => (
//                 <Card key={msg.messageGuid} className="mb-3">
//                   <Card.Body>
//                     <div className="d-flex justify-content-between mb-2">
//                       <div>
//                         <strong>{msg.fromName || msg.fromMail}</strong>
//                         <div className="text-muted small">
//                           {moment(msg.createdOnDate).format("MMM DD, YYYY")}{" "}
//                           {msg.createdOnTime?.substring(0, 8)}
//                         </div>
//                       </div>

//                       {msg.isInternal && (
//                         <Badge bg="warning">Internal Note</Badge>
//                       )}
//                     </div>

//                     <div
//                       className="conversation-body"
//                       dangerouslySetInnerHTML={{
//                         __html: msg.rawHtmlBody || msg.body,
//                       }}
//                     />

//                     {/* Attachments for this message */}
//                     {attachments.filter((a) => a.ticketMessageId === msg.id)
//                       .length > 0 && (
//                       <div className="mt-2">
//                         <strong>Attachments:</strong>
//                         {attachments
//                           .filter((a) => a.ticketMessageId === msg.id)
//                           .map((a) => (
//                             <div key={a.attachmentGuid}>📎 {a.fileName}</div>
//                           ))}
//                       </div>
//                     )}
//                   </Card.Body>
//                 </Card>
//               ))
//             ) : (
//               <EmptyState text="No messages found." />
//             )}
//           </Section>

//           {/* <Section title="✍️ Reply">
//             <Form>
//               <Form.Group className="mb-3">
//                 <Form.Control
//                   as="textarea"
//                   rows={4}
//                   value={replyBody}
//                   onChange={(e) => setReplyBody(e.target.value)}
//                 />
//               </Form.Group>

//               <Button onClick={handleSendReply}>Send Reply</Button>
//             </Form>
//           </Section> */}

//           {/* ================= ACTIVITY LOG ================= */}
//           <Section title="📜 Ticket Activity Log">
//             {ticketLogs.length > 0 ? (
//               <Table responsive bordered hover size="sm" className="mt-2">
//                 <thead className="table-light">
//                   <tr>
//                     <th>#</th>
//                     <th>Action</th>
//                     <th>Old</th>
//                     <th>New</th>
//                     <th>Worked By</th>
//                     <th>Effort</th>
//                     <th>Remarks</th>
//                     <th>Date</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {ticketLogs.map((log, idx) => (
//                     <tr key={log.ticketLogGuid}>
//                       <td>{idx + 1}</td>
//                       <td>
//                         <Badge bg="secondary">
//                           {log.actionType?.replace(/_/g, " ")}
//                         </Badge>
//                       </td>
//                       <td>{log.oldValue || "—"}</td>
//                       <td>{log.newValue || "—"}</td>
//                       <td>{log.changedBy}</td>
//                       <td>
//                         {log.effortValue
//                           ? `${log.effortValue} ${log.effortUnit}`
//                           : "—"}
//                       </td>
//                       <td className="text-muted">
//                         {log.closureRemarks || "—"}
//                       </td>
//                       <td>
//                         {formatDateTime(log.createdDate, log.createdTime)}
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </Table>
//             ) : (
//               <EmptyState text="No ticket activity found." />
//             )}
//           </Section>

//           {/* ================= TASKS ================= */}
//           <Section title="🧩 Related Tasks">
//             {ticketTasks.length > 0 ? (
//               ticketTasks.map((task) => (
//                 <Card key={task.taskId} className="task-card mb-3">
//                   <Card.Body>
//                     <div className="d-flex justify-content-between align-items-center mb-2">
//                       <div>
//                         <strong>{task.taskName}</strong>
//                         <Badge bg="dark" className="ms-2">
//                           {task.taskId}
//                         </Badge>
//                       </div>
//                       <div className="d-flex gap-2">
//                         {getStatusBadge(task.taskStatus)}
//                         {getPriorityBadge(task.taskPriority)}
//                       </div>
//                     </div>

//                     <div className="text-muted mb-2">
//                       Assigned To: {task.assignedTo || "N/A"}
//                     </div>

//                     {task.logs?.length > 0 ? (
//                       <Table bordered size="sm">
//                         <thead className="table-light">
//                           <tr>
//                             <th>#</th>
//                             <th>Type</th>
//                             <th>Description</th>
//                             <th>User</th>
//                             <th>Date</th>
//                           </tr>
//                         </thead>
//                         <tbody>
//                           {task.logs.map((l, i) => (
//                             <tr key={l.taskLogId}>
//                               <td>{i + 1}</td>
//                               <td>
//                                 <Badge bg="secondary">
//                                   {l.logType?.replace(/_/g, " ")}
//                                 </Badge>
//                               </td>
//                               <td>{l.logDescription || "—"}</td>
//                               <td>{l.assignedUserId}</td>
//                               <td>
//                                 {formatDateTime(l.createdDate, l.createdTime)}
//                               </td>
//                             </tr>
//                           ))}
//                         </tbody>
//                       </Table>
//                     ) : (
//                       <EmptyState text="No logs recorded for this task." />
//                     )}
//                   </Card.Body>
//                 </Card>
//               ))
//             ) : (
//               <EmptyState text="No tasks created for this ticket." />
//             )}
//           </Section>
//         </Card.Body>
//       </Card>

//       <TicketEditModal
//         show={showEditModal}
//         onClose={() => setShowEditModal(false)}
//         ticket={ticket}
//         orgId={user.organisationId}
//         userDetails={user}
//         projectOptions={projectOptions}
//         ticketAssignees={ticketAssignees}
//         refreshTickets={refreshTicketData}
//       />
//     </Container>
//   );
// };

// export default TicketInfoPage;
